import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { supabase } from '@/lib/supabase'
import type { Constitution } from '@/types'

// --- Collapsible sections logic ---
const COLLAPSED_KEY = 'tradingApp_constitutionCollapsed'

function loadCollapsedState(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSED_KEY)
    if (raw) return new Set(JSON.parse(raw))
  } catch { /* ignore */ }
  return new Set()
}

function saveCollapsedState(collapsed: Set<string>) {
  localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...collapsed]))
}

interface MarkdownSection {
  id: string
  level: number
  heading: string
  ownContent: string // content directly under this heading (before next heading at any level)
  children: MarkdownSection[]
}

function parseMarkdownSections(content: string): MarkdownSection[] {
  const lines = content.split('\n')
  const root: MarkdownSection[] = []
  const stack: { level: number; section: MarkdownSection; children: MarkdownSection[] }[] = []

  let currentLines: string[] = []
  let idCounter = 0

  const flush = () => {
    const text = currentLines.join('\n')
    currentLines = []
    return text
  }

  const makeId = (heading: string) => {
    idCounter++
    return `s-${idCounter}-${heading.slice(0, 40).replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/)

    if (headingMatch) {
      // Flush current content to the top of stack
      const ownText = flush()
      if (stack.length > 0) {
        stack[stack.length - 1].section.ownContent += ownText
      } else if (root.length > 0) {
        root[root.length - 1].ownContent += ownText
      } else if (ownText.trim()) {
        // Content before any heading — create a virtual root
        root.push({ id: makeId('preamble'), level: 0, heading: '', ownContent: ownText, children: [] })
      }

      const level = headingMatch[1].length
      const heading = headingMatch[2].trim()
      const newSection: MarkdownSection = { id: makeId(heading), level, heading, ownContent: '\n', children: [] }

      // Pop stack until we find a parent with lower level
      while (stack.length > 0 && stack[stack.length - 1].level >= level) {
        stack.pop()
      }

      if (stack.length === 0) {
        root.push(newSection)
      } else {
        stack[stack.length - 1].section.children.push(newSection)
      }

      stack.push({ level, section: newSection, children: newSection.children })
    } else {
      currentLines.push(line)
    }
  }

  // Flush remaining
  const remaining = flush()
  if (stack.length > 0) {
    stack[stack.length - 1].section.ownContent += remaining
  } else if (root.length > 0) {
    root[root.length - 1].ownContent += remaining
  } else if (remaining.trim()) {
    root.push({ id: `s-${++idCounter}-tail`, level: 0, heading: '', ownContent: remaining, children: [] })
  }

  return root
}

function CollapsibleSection({ section, collapsed, toggleCollapse, depth = 0 }: {
  section: MarkdownSection
  collapsed: Set<string>
  toggleCollapse: (id: string) => void
  depth?: number
}) {
  const isCollapsed = collapsed.has(section.id)

  if (!section.heading) {
    // Preamble / no-heading content
    return (
      <div className="report-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.ownContent}</ReactMarkdown>
      </div>
    )
  }

  const HeadingTag = `h${section.level}` as keyof JSX.IntrinsicElements
  const chevron = isCollapsed ? '▶' : '▼'

  return (
    <div className={`${depth > 0 ? '' : ''}`}>
      {/* Clickable heading row */}
      <div
        className="flex items-center gap-2 cursor-pointer select-none group hover:bg-gray-100 rounded px-1 -mx-1 transition-colors"
        onClick={() => toggleCollapse(section.id)}
      >
        <span className={`text-gray-400 text-xs w-4 shrink-0 transition-transform ${isCollapsed ? '' : ''}`}>
          {chevron}
        </span>
        <div className="report-content flex-1">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{`${'#'.repeat(section.level)} ${section.heading}`}</ReactMarkdown>
        </div>
      </div>

      {/* Content (hidden when collapsed) */}
      {!isCollapsed && (
        <div className={`${section.level >= 2 ? 'ml-4 border-l border-gray-200 pl-3' : ''}`}>
          {section.ownContent.trim() && (
            <div className="report-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.ownContent}</ReactMarkdown>
            </div>
          )}
          {section.children.map(child => (
            <CollapsibleSection
              key={child.id}
              section={child}
              collapsed={collapsed}
              toggleCollapse={toggleCollapse}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function ConstitutionPage() {
  const [constitution, setConstitution] = useState<Constitution | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<number[]>([])
  const [activeResult, setActiveResult] = useState(0)
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadCollapsedState())
  const fileInputRef = useRef<HTMLInputElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      saveCollapsedState(next)
      return next
    })
  }, [])

  const collapseAll = useCallback(() => {
    if (!constitution?.content) return
    const sections = parseMarkdownSections(constitution.content)
    const allIds = new Set<string>()
    const collectIds = (secs: MarkdownSection[]) => {
      for (const s of secs) {
        if (s.heading) allIds.add(s.id)
        collectIds(s.children)
      }
    }
    collectIds(sections)
    setCollapsed(allIds)
    saveCollapsedState(allIds)
  }, [constitution])

  const expandAll = useCallback(() => {
    setCollapsed(new Set())
    saveCollapsedState(new Set())
  }, [])

  const fetchConstitution = useCallback(async () => {
    try {
      const { data, error: err } = await supabase
        .from('constitution')
        .select('*')
        .eq('is_active', true)
        .single()
      if (err) throw new Error(err.message)
      setConstitution(data as unknown as Constitution)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load constitution')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConstitution()
  }, [fetchConstitution])

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    if (!file.name.endsWith('.md') && !file.name.endsWith('.txt') && !file.name.endsWith('.markdown')) {
      setError('Only .md, .markdown, or .txt files are accepted.')
      return
    }
    if (file.size > 100_000) {
      setError('File too large. Maximum 100,000 characters.')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const content = await file.text()
      if (!content.trim()) { setError('File is empty.'); setUploading(false); return }

      const { data: updated, error: putErr } = await supabase
        .from('constitution')
        .update({ content, updated_at: new Date().toISOString() })
        .eq('is_active', true)
        .select()
        .single()
      if (putErr) throw new Error(putErr.message)
      setConstitution(updated as unknown as Constitution)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  // Search: highlight and scroll to matches
  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (!query.trim() || !contentRef.current) {
      setSearchResults([])
      setActiveResult(0)
      // Remove existing highlights
      if (contentRef.current) {
        const marks = contentRef.current.querySelectorAll('mark[data-search]')
        marks.forEach(m => {
          const parent = m.parentNode
          if (parent) {
            parent.replaceChild(document.createTextNode(m.textContent || ''), m)
            parent.normalize()
          }
        })
      }
      return
    }

    // Use browser's built-in text search via TreeWalker
    const container = contentRef.current
    // Remove previous highlights first
    const existingMarks = container.querySelectorAll('mark[data-search]')
    existingMarks.forEach(m => {
      const parent = m.parentNode
      if (parent) {
        parent.replaceChild(document.createTextNode(m.textContent || ''), m)
        parent.normalize()
      }
    })

    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
    const matchPositions: Element[] = []
    const lowerQuery = query.toLowerCase()

    const textNodes: Text[] = []
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text)
    }

    for (const textNode of textNodes) {
      const text = textNode.textContent || ''
      const lowerText = text.toLowerCase()
      let startIdx = 0
      const fragments: (string | { match: string })[] = []
      let hasMatch = false

      while (true) {
        const idx = lowerText.indexOf(lowerQuery, startIdx)
        if (idx === -1) {
          fragments.push(text.slice(startIdx))
          break
        }
        hasMatch = true
        if (idx > startIdx) fragments.push(text.slice(startIdx, idx))
        fragments.push({ match: text.slice(idx, idx + query.length) })
        startIdx = idx + query.length
      }

      if (hasMatch && textNode.parentNode) {
        const span = document.createElement('span')
        for (const frag of fragments) {
          if (typeof frag === 'string') {
            span.appendChild(document.createTextNode(frag))
          } else {
            const mark = document.createElement('mark')
            mark.setAttribute('data-search', 'true')
            mark.style.background = '#fbbf24'
            mark.style.color = '#000'
            mark.style.padding = '0 2px'
            mark.style.borderRadius = '2px'
            mark.textContent = frag.match
            span.appendChild(mark)
            matchPositions.push(mark)
          }
        }
        textNode.parentNode.replaceChild(span, textNode)
      }
    }

    setSearchResults(matchPositions.map((_, i) => i))
    setActiveResult(0)

    // Scroll to first match
    if (matchPositions.length > 0) {
      matchPositions[0].scrollIntoView({ behavior: 'smooth', block: 'center' })
      ;(matchPositions[0] as HTMLElement).style.background = '#f97316'
    }
  }

  const jumpToResult = (direction: 'next' | 'prev') => {
    if (!contentRef.current || searchResults.length === 0) return
    const marks = contentRef.current.querySelectorAll('mark[data-search]')
    if (marks.length === 0) return

    // Reset current highlight
    marks.forEach(m => ((m as HTMLElement).style.background = '#fbbf24'))

    let newIdx = activeResult
    if (direction === 'next') {
      newIdx = (activeResult + 1) % marks.length
    } else {
      newIdx = (activeResult - 1 + marks.length) % marks.length
    }

    setActiveResult(newIdx)
    const target = marks[newIdx] as HTMLElement
    target.style.background = '#f97316'
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400 text-lg">Loading constitution...</div>
      </div>
    )
  }

  if (error && !constitution) {
    return (
      <div className="p-6">
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 text-red-300">{error}</div>
      </div>
    )
  }

  return (
    <div className="p-6 mx-auto relative" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">📜 Constitution</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 transition-colors">
            {uploading ? 'Uploading...' : '📂 Upload .md'}
          </button>
          <input ref={fileInputRef} type="file" accept=".md,.markdown,.txt" onChange={handleFileUpload} className="hidden" />
          <button onClick={() => {
            if (!constitution?.content) return
            const blob = new Blob([constitution.content], { type: 'text/markdown' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `Constitution_${new Date().toISOString().slice(0,10)}.md`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
          }} disabled={!constitution?.content}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 transition-colors">
            ⬇ Export .md
          </button>
          <button onClick={async () => {
            alert('Feature not available in v2')
          }} disabled={!constitution?.content}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-purple-700 text-purple-100 hover:bg-purple-600 disabled:opacity-50 transition-colors">
            🔄 Push Cache
          </button>
          <button onClick={() => navigate('/portfolio-manager/constitution/edit')}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
            ✏️ Edit
          </button>
        </div>
      </div>

      {/* Persistent search bar — sticky */}
      <div className="sticky top-0 z-10 bg-[var(--color-bg-primary)] pb-3 pt-1 -mx-6 px-6 border-b border-transparent" style={{ backdropFilter: 'blur(8px)' }}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search constitution..."
              className="w-full px-4 py-2 pl-9 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">🔍</span>
          </div>
          {searchQuery && (
            <div className="flex items-center gap-1 text-xs text-gray-400">
              <span>{searchResults.length > 0 ? `${activeResult + 1}/${contentRef.current?.querySelectorAll('mark[data-search]').length || 0}` : '0 results'}</span>
              <button onClick={() => jumpToResult('prev')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">↑</button>
              <button onClick={() => jumpToResult('next')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">↓</button>
              <button onClick={() => handleSearch('')} className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white">✕</button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-900/30 border border-red-700 rounded-lg p-3 text-red-300 text-sm">{error}</div>
      )}

      {constitution?.updatedAt && (
        <div className="mb-4 text-xs text-gray-500">
          Last updated: {new Date(constitution.updatedAt).toLocaleString()}
        </div>
      )}

      {/* Document */}
      {constitution && constitution.content ? (
        <div ref={contentRef}>
          {/* Collapse/Expand controls */}
          <div className="flex items-center gap-2 mb-3">
            <button onClick={collapseAll} className="px-2 py-1 text-[10px] rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">
              Collapse All
            </button>
            <button onClick={expandAll} className="px-2 py-1 text-[10px] rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">
              Expand All
            </button>
            <span className="text-[10px] text-gray-400">Click any heading to collapse/expand</span>
          </div>
          {parseMarkdownSections(constitution.content).map(section => (
            <CollapsibleSection
              key={section.id}
              section={section}
              collapsed={collapsed}
              toggleCollapse={toggleCollapse}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center text-gray-400">
          <p className="mb-4">No constitution uploaded yet.</p>
          <p className="text-sm">Click "📂 Upload .md" to load your Portfolio Manager Constitution, or "✏️ Edit" to write it directly.</p>
        </div>
      )}

      {/* Scroll to top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-6 right-6 w-10 h-10 rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-500 flex items-center justify-center text-lg z-50 transition-colors"
        title="Scroll to top"
      >
        ↑
      </button>
    </div>
  )
}
